import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { toJS } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useStore } from '../../../../stores/store';
import ListLayout from '../../../Generic/ListLayout';
import DataTable from '../../../Generic/DataTable';
import confirmDeleteDialog from '../../../Generic/confirmDeleteDialog';
import useAxiosPrivate from "../../../../hooks/useAxiosPrivate";
import useDeleteItem from '../../../../hooks/useDeleteItem';
import useFilterData from '../../../../hooks/useFilterData';
import useFetchData from '../../../../hooks/useFetchData';
import useLocalizedTextsMap from '../../../../hooks/useLocalizedTextsMap';
import { useSetMenu } from "../../../../hooks/useSetMenu";
import { createActiveColumn, createActionColumn, createGeneralColumn } from '../../../Generic/dataGridColumns';

const List = () => {
  const axiosPrivate = useAxiosPrivate();
  const { mainStore, researchTeamMemberStore } = useStore();
  const { t } = useTranslation();
  const localizedTextsMap = useLocalizedTextsMap();
  useSetMenu(mainStore.ResearchTeamAdminMenuType, mainStore.ResearchTeamAdminMenuNo);
  const [columns, setColumns] = useState([]);
  const [pageSize, setPageSize] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");

  useFetchData(axiosPrivate, `/ResearchTeamMembers`, researchTeamMemberStore);
  const filteredData = useFilterData(toJS(researchTeamMemberStore.researchTeamMembers), searchQuery);

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  const deleteItem = useDeleteItem(axiosPrivate, '/ResearchTeamMembers', researchTeamMemberStore.deleteItem);

  const remove = useCallback((id) => {
    confirmDeleteDialog(t, deleteItem, id);
  }, [deleteItem, t]);

  const cols = useMemo(() => [
    createGeneralColumn("memberOrder", "order", t, 80),
    {
      field: 'avatar',
      headerName: t("avatar"),
      width: 80,
      headerClassName: 'tableHeaderClassName',
      cellClassName: 'tableCellClassName',
      headerAlign: "center",
      align: "center",
      renderHeader: (params) => (
        <span style={{ fontWeight: 600 }}>{params.colDef.headerName}</span>
      ),
      renderCell: (params) => {
        const avatarPath = params.row.avatarUrl;
        const avatarColor = params.row.avatarColor || '#112d4e';
        const imageUrl = avatarPath ? `${process.env.REACT_APP_SERVER_URLForLogo}/${avatarPath.replace(/\\/g, '/')}` : null;

        return (
          <div
            className="rounded-circle d-flex align-items-center justify-content-center"
            style={{
              width: '40px',
              height: '40px',
              backgroundColor: avatarColor,
              overflow: 'hidden'
            }}
          >
            {imageUrl ? (
              <img src={imageUrl} alt="avatar"
                style={{ width: '40px', height: '40px', objectFit: 'cover' }}
                onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'block'; }}
              />
            ) : null}
            <i className="bi bi-person-circle text-white" style={{ fontSize: '1.2rem', display: imageUrl ? 'none' : 'block' }}></i>
          </div>
        );
      }
    },
    createGeneralColumn("nameEN", "name", t, 150),
    createGeneralColumn("titleEN", "title", t, 200),
    createGeneralColumn("memberType", "memberType", t, 120),
    createActiveColumn(t),
    createActionColumn(t, "/Admin/ResearchTeam/Edit", remove),
  ].filter(Boolean), [t, remove]);

  useEffect(() => {
    setColumns(cols);
    // eslint-disable-next-line
  }, [t]);

  return (
    <>
      <ListLayout
        title={t("researchTeamListTitle") || "Research Team Members"}
        searchQuery={searchQuery}
        onSearchChange={handleSearchChange}
        searchPlaceholder={t("searchPlaceholder")}
        addButtonLink="/Admin/ResearchTeam/Add"
        addButtonTitle={t("add")}
      >
        <DataTable
          rows={filteredData}
          columns={columns}
          pageSize={pageSize}
          setPageSize={setPageSize}
          localeTextsMap={localizedTextsMap}
        />
      </ListLayout>
    </>
  );
};

export default observer(List);
